<template>
    <div class="email-preview-card border rounded mb-4" :style="cardStyle">
        <!-- Email Header -->
        <div class="email-header p-3 position-relative"
            style="background-color: #D7D7D7; border-bottom: 1px solid #E0E0E0;">
            <!-- Corner Ribbon -->
            <div class="corner-ribbon" :style="ribbonStyle">SAMPLE</div>
            <div style="font-size: 13px;">
                <div><span style="width: 60px; display: inline-block;">From:</span>
                    <b>no-reply@fastvisa.us</b>
                </div>
                <div class="d-flex mt-1 align-items-center">
                    <span style="width: 60px; display: inline-block;">To:</span>
                    <!-- Recipient line that truncates based on available width -->
                    <div class="d-flex align-items-center flex-wrap recipient-line" ref="recipientContainer">
                        <span
                            ref="recipientText"
                            class="recipient-text"
                            :style="recipientTextStyle"
                        >
                            <template v-if="visibleRecipientItems.length > 0">
                                <span
                                    v-for="(recipient, idx) in visibleRecipientItems"
                                    :key="`${recipient.id || recipient.email || 'recipient'}-${idx}`"
                                >
                                    <span>{{ recipient.email }}</span>
                                    <span v-if="isRecipientInactive(recipient)" class="recipient-inactive-label">(Inactive)</span>
                                    <span v-if="idx < visibleRecipientItems.length - 1">, </span>
                                </span>
                            </template>
                            <template v-else>-</template>
                        </span>
                        <span v-if="moreRecipientItems.length > 0" class="recipient-ellipsis">{{ ellipsisToken }}</span>
                        <!-- Show overflow count and tooltip only when there are hidden recipients -->
                        <RecipientTooltip v-if="moreRecipientItems.length > 0" :hoverMode="true" direction="bottom">
                            <template #trigger>
                                <span class="text-primary ml-1">+{{ moreRecipientItems.length }} more</span>
                            </template>
                            <template #text>
                                <div class="recipient-tooltip">
                                    <div v-for="(recipient, idx) in moreRecipientItems" :key="idx" class="recipient-tooltip__item">
                                        <span class="recipient-tooltip__email">{{ recipient.email }}</span>
                                        <span v-if="isRecipientInactive(recipient)" class="recipient-inactive-label">(Inactive)</span>
                                    </div>
                                </div>
                            </template>
                        </RecipientTooltip>
                    </div>
                </div>
                <div class="mt-1 email-header-row">
                    <span class="email-header-label">Subject:</span>
                    <b class="email-header-value">{{ reportData.email_settings.subject }}</b>
                </div>
            </div>
        </div>

        <!-- Email Body -->
        <div class="email-body p-4 bg-white">
            <!-- Logo -->
            <div class="text-center mb-4" v-if="shouldShowLogo">
                <img :src="headerLogoUrl" alt="Logo" style="max-height: 50px;">
            </div>

            <!-- Content -->
            <div class="email-content mb-4" v-html="sanitizedBody"></div>

            <hr class="my-4" style="border-top: 1px solid #D7D7D7;">

            <!-- Footer Info -->
            <div class="text-muted" style="font-size: 12px;">
                <span style="color: #848484;">This automated report was scheduled by</span> <b>{{ reportData.created_by || 'Me' }}</b>
            </div>
            <hr class="my-4" style="border-top: 1px solid #D7D7D7;">

            <!-- Attachment Mockup -->
            <div class="attachment-mockup mt-3 p-3 border rounded d-flex align-items-center justify-content-between"
                style="background-color: #fff;height: 40px;">
                <div class="attachment-info d-flex align-items-center">
                    <v-icon color="#757575" class="mr-2">mdi-file-document-outline</v-icon>
                    <v-tooltip v-if="isAttachmentTruncated" bottom content-class="attachment-tooltip">
                        <template v-slot:activator="{ on, attrs }">
                            <span
                                class="attachment-name"
                                v-bind="attrs"
                                v-on="on"
                                style="font-size: 14px; color: #3F3F3F;"
                                ref="attachmentName"
                            >
                                {{ attachmentName }}
                            </span>
                        </template>
                        <span>{{ attachmentName }}</span>
                    </v-tooltip>
                    <span v-else class="attachment-name" style="font-size: 14px; color: #3F3F3F;" ref="attachmentName">
                        {{ attachmentName }}
                    </span>
                </div>
                <div class="badge badge-success px-2 attachment-badge"
                    style="font-size: 14px; background-color: #3DA568; color: white;border-radius: 11px;">CSV</div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import moment from 'moment-timezone';
import RecipientTooltip from './RecipientTooltip.vue';

const FIXED_TIME_ZONE = 'Etc/GMT+5';
const ELLIPSIS_TOKEN = ' ...';

export default {
    name: 'EmailPreviewCard',
    components: { RecipientTooltip },
    props: {
        reportData: {
            type: Object,
            required: true,
            default: () => ({ email_settings: {} })
        },
        cardStyle: {
            type: Object,
            default: () => ({})
        },
        ribbonTop: {
            type: String,
            default: '25px'
        },
        ribbonBgColor: {
            type: String,
            default: '#848484'
        },
        hideLogoWhenEmbedded: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            // Recipient texts that fit in the available width
            visibleRecipientEmails: [],
            // Width available for the email text (pixels)
            availableEmailWidth: null,
            // Debounce timer for recalculation
            recalcTimer: null,
            // Resize observer for responsive recalculation
            resizeObserver: null,
            // Canvas used to measure text width
            textMeasureCanvas: null,
            // Cached measurements to avoid repeated work
            recipientMetrics: {
                font: null,
                emailKey: '',
                emailWidths: [],
                prefixSums: [],
                separatorWidth: 0,
            },
            // Cached key for current recipient list
            recipientEmailKey: '',
            // Force a fixed width only when the text itself overflows
            recipientForceWidth: false,
            // Attachment truncation state
            isAttachmentTruncated: false,
            attachmentTruncateTimer: null,
        };
    },
    computed: {
        ...mapGetters({
            logoPaths: 'logoPaths',
        }),
        recipientList() {
            return (this.reportData?.email_settings?.recipients || [])
                .map((recipient) => ({
                    id: recipient?.id,
                    email: recipient?.email,
                    status: recipient?.status,
                }))
                .filter((recipient) => recipient.email);
        },
        // Full list of recipient emails
        recipientEmails() {
            return this.recipientList.map((recipient) => recipient.email);
        },
        // Full list of recipient display text (include inactive marker when needed)
        recipientDisplayTexts() {
            return this.recipientList.map((recipient) => this.recipientDisplayText(recipient));
        },
        // Recipients that are actually rendered in the line
        visibleRecipientItems() {
            if (this.recipientList.length === 0) return [];
            if (this.visibleRecipientEmails.length > 0) {
                return this.recipientList.slice(0, this.visibleRecipientEmails.length);
            }
            return this.recipientList;
        },
        // Remaining emails that go into the "+N more" tooltip
        moreRecipientEmails() {
            return this.recipientEmails.slice(this.visibleRecipientEmails.length);
        },
        moreRecipientItems() {
            return this.recipientList.slice(this.visibleRecipientEmails.length);
        },
        // Dynamic max width for the recipient text (ensures ellipsis before the SAMPLE ribbon).
        recipientTextStyle() {
            const maxWidth = this.availableEmailWidth
                ? `${this.availableEmailWidth}px`
                : (this.moreRecipientEmails.length > 0 ? '360px' : '600px');
            const style = {
                maxWidth,
                minWidth: 0,
            };
            // Force a fixed width only when the text overflows, so we avoid blank gaps.
            if (this.availableEmailWidth && this.recipientForceWidth) {
                style.width = maxWidth;
            }
            return style;
        },
        ellipsisToken() {
            return ELLIPSIS_TOKEN;
        },
        headerLogoUrl() {
            const logo = this.reportData?.email_settings?.header_logo;
            if (!logo || logo === 'none') return null;
            const fallbackFastvisa = `${window.location.origin}/assets/logos/logo-fastvisa.png`;
            const fallbackCharter = `${window.location.origin}/assets/logos/logo-charter.png`;
            const resolvedFastvisa = this.resolveLogoUrl(this.logoPaths?.fastvisa) || fallbackFastvisa;
            const resolvedCharter = this.resolveLogoUrl(this.logoPaths?.charter) || fallbackCharter;
            if (logo === 'fastvisa') return resolvedFastvisa;
            if (logo === 'charter') return resolvedCharter;
            return `${window.location.origin}/assets/logos/${logo}-logo.png`;
        },
        sanitizedBody() {
            // Basic check, v-html requires safe content but for preview it's fine as it's user input
            return this.reportData?.email_settings?.body || '';
        },
        hasEmbeddedLogo() {
            return /data-scheduled-report-logo=["']true["']/i.test(this.sanitizedBody || '');
        },
        shouldShowLogo() {
            if (!this.headerLogoUrl) return false;
            if (!this.hideLogoWhenEmbedded) return true;
            return !this.hasEmbeddedLogo;
        },
        attachmentName() {
            const name = this.reportData?.name || 'Report';
            const frequency = (this.reportData?.schedule_type || '').toLowerCase();
            const frequencyLabel = frequency === 'monthly'
                ? 'Monthly'
                : frequency === 'weekly'
                    ? 'Weekly'
                    : '';
            const scheduleDay = this.reportData?.schedule_day || 'monday';
            const scheduleTime = this.reportData?.schedule_time || '09:00';
            // FE note: schedule_day/time are client-only inputs (backend stores next_run_at/last_run_at).
            // This filename is computed on the client to match the preview date.
            const timeParts = scheduleTime.split(':');
            const hours = parseInt(timeParts[0], 10) || 9;
            const minutes = parseInt(timeParts[1], 10) || 0;
            const now = moment().tz(FIXED_TIME_ZONE);
            let nextDate = moment().tz(FIXED_TIME_ZONE);
            nextDate = nextDate.hours(hours).minutes(minutes).seconds(0).milliseconds(0);

            if (frequency === 'weekly') {
                const targetDay = now.day(scheduleDay).isoWeekday();
                nextDate = moment().tz(FIXED_TIME_ZONE)
                    .isoWeekday(targetDay)
                    .hours(hours)
                    .minutes(minutes)
                    .seconds(0)
                    .milliseconds(0);
                // Always schedule the next occurrence, not the same day.
                if (nextDate.isSame(now, 'day') || nextDate.isBefore(now)) {
                    nextDate.add(1, 'week');
                }
            } else if (frequency === 'monthly') {
                nextDate = moment().tz(FIXED_TIME_ZONE)
                    .date(1)
                    .hours(hours)
                    .minutes(minutes)
                    .seconds(0)
                    .milliseconds(0);
                if (nextDate.isBefore(now)) {
                    nextDate.add(1, 'month');
                }
            }

            const dateLabel = nextDate.format('MM-DD-YYYY');
            const suffix = frequencyLabel
                ? ` - ${frequencyLabel} Report ${dateLabel}`
                : ` - ${dateLabel}`;
            return `${name}${suffix}.csv`;
        },
        ribbonStyle() {
            return {
                top: this.ribbonTop,
                backgroundColor: this.ribbonBgColor,
            };
        }
    },
    watch: {
        attachmentName() {
            this.scheduleUpdateAttachmentTruncation();
        },
        // Recalculate whenever recipient list changes
        recipientDisplayTexts: {
            immediate: true,
            handler(newVal) {
                const list = Array.isArray(newVal) ? newVal : [];
                this.recipientEmailKey = list.join('\u0000');
                this.visibleRecipientEmails = list.slice();
                this.scheduleRecalculateRecipients();
            },
        },
    },
    methods: {
        isRecipientInactive(recipient) {
            if (!recipient) return false;
            const status = (recipient?.status || '').toString().toLowerCase();
            return status === 'inactive';
        },
        recipientDisplayText(recipient) {
            const email = recipient?.email || '';
            if (!email) return '';
            return this.isRecipientInactive(recipient) ? `${email} (Inactive)` : email;
        },
        scheduleUpdateAttachmentTruncation() {
            if (this.attachmentTruncateTimer) {
                clearTimeout(this.attachmentTruncateTimer);
            }
            this.attachmentTruncateTimer = setTimeout(() => {
                this.updateAttachmentTruncation();
            }, 50);
        },
        updateAttachmentTruncation() {
            this.$nextTick(() => {
                const ref = this.$refs.attachmentName;
                const el = Array.isArray(ref) ? ref[0] : ref;
                if (!el) {
                    this.isAttachmentTruncated = false;
                    return;
                }
                this.isAttachmentTruncated = el.scrollWidth > el.clientWidth;
            });
        },
        handleResize() {
            this.scheduleRecalculateRecipients();
            this.scheduleUpdateAttachmentTruncation();
        },
        // Debounced recalculation to avoid excessive DOM reads
        scheduleRecalculateRecipients() {
            if (this.recalcTimer) {
                clearTimeout(this.recalcTimer);
            }
            this.recalcTimer = setTimeout(() => {
                this.recalculateRecipients();
            }, 50);
        },
        // Compute how many emails fit in the available container width
        recalculateRecipients() {
            this.$nextTick(() => {
                const recipientTexts = this.recipientDisplayTexts || [];
                if (recipientTexts.length === 0) {
                    this.visibleRecipientEmails = [];
                    this.availableEmailWidth = null;
                    this.recipientForceWidth = false;
                    return;
                }

                const container = this.$refs.recipientContainer;
                const textEl = this.$refs.recipientText;
                if (!container || !textEl) {
                    this.visibleRecipientEmails = recipientTexts.slice();
                    this.availableEmailWidth = null;
                    this.recipientForceWidth = false;
                    return;
                }

                const containerWidth = Math.floor(container.getBoundingClientRect().width);
                if (!containerWidth) {
                    this.visibleRecipientEmails = recipientTexts.slice();
                    this.availableEmailWidth = null;
                    this.recipientForceWidth = false;
                    return;
                }

                // Use actual computed font to measure text width correctly
                const metricsReady = this.updateRecipientMetrics(recipientTexts, textEl);
                if (!metricsReady) {
                    this.visibleRecipientEmails = recipientTexts.slice();
                    this.availableEmailWidth = null;
                    this.recipientForceWidth = false;
                    return;
                }

                const { font, prefixSums, separatorWidth } = this.recipientMetrics;
                const total = recipientTexts.length;

                const measure = (text) => this.measureTextWidth(text, font);
                const moreWidthCache = {};
                const ellipsisWidth = measure(ELLIPSIS_TOKEN);
                // Width reserved for the "+N more" label
                const moreWidth = (hiddenCount) => {
                    if (hiddenCount <= 0) return 0;
                    if (moreWidthCache[hiddenCount]) return moreWidthCache[hiddenCount];
                    const text = `+${hiddenCount} more`;
                    const marginLeft = 4;
                    const width = measure(text) + marginLeft;
                    moreWidthCache[hiddenCount] = width;
                    return width;
                };

                const textWidth = (count) => {
                    if (count <= 0) return 0;
                    return prefixSums[count] + separatorWidth * (count - 1);
                };
                const availableWidth = (hiddenCount) => {
                    const reserved = moreWidth(hiddenCount) + (hiddenCount > 0 ? ellipsisWidth : 0);
                    return Math.max(0, containerWidth - reserved);
                };

                const fullTextWidth = textWidth(total);
                const fullAvailable = availableWidth(0);
                if (fullTextWidth <= fullAvailable) {
                    this.visibleRecipientEmails = recipientTexts.slice();
                    this.availableEmailWidth = fullAvailable;
                    this.recipientForceWidth = false;
                    return;
                }

                // Find the largest number of emails that still fits
                let bestCount = 1;
                let bestWidth = availableWidth(total - 1);

                for (let count = total - 1; count >= 1; count--) {
                    const hidden = total - count;
                    const available = availableWidth(hidden);
                    const currentWidth = textWidth(count);
                    if (currentWidth <= available) {
                        bestCount = count;
                        bestWidth = available;
                        break;
                    }
                }

                this.visibleRecipientEmails = recipientTexts.slice(0, bestCount);
                this.availableEmailWidth = bestWidth;
                this.recipientForceWidth = textWidth(bestCount) > bestWidth;
            });
        },
        // Cache widths of emails for faster recalculation
        updateRecipientMetrics(emails, textEl) {
            if (!textEl) return false;
            const font = this.getFontString(textEl);
            const emailKey = this.recipientEmailKey || emails.join('\u0000');

            if (
                this.recipientMetrics.font === font &&
                this.recipientMetrics.emailKey === emailKey
            ) {
                return true;
            }

            const separatorWidth = this.measureTextWidth(', ', font);
            const emailWidths = emails.map((email) => this.measureTextWidth(email, font));
            const prefixSums = new Array(emailWidths.length + 1);
            prefixSums[0] = 0;
            for (let i = 0; i < emailWidths.length; i++) {
                prefixSums[i + 1] = prefixSums[i] + emailWidths[i];
            }

            this.recipientMetrics = {
                font,
                emailKey,
                emailWidths,
                prefixSums,
                separatorWidth,
            };
            return true;
        },
        // Build a canvas font string from the rendered element styles
        getFontString(element) {
            const style = window.getComputedStyle(element);
            const fontStyle = style.fontStyle || 'normal';
            const fontVariant = style.fontVariant || 'normal';
            const fontWeight = style.fontWeight || 'normal';
            const fontSize = style.fontSize || '14px';
            const fontFamily = style.fontFamily || 'sans-serif';
            return `${fontStyle} ${fontVariant} ${fontWeight} ${fontSize} ${fontFamily}`;
        },
        // Measure a string width using canvas
        measureTextWidth(text, font) {
            if (!this.textMeasureCanvas) {
                this.textMeasureCanvas = document.createElement('canvas');
            }
            const context = this.textMeasureCanvas.getContext('2d');
            context.font = font;
            return context.measureText(text).width;
        },
        resolveLogoUrl(path) {
            if (!path) return null;
            if (path.startsWith('http://') || path.startsWith('https://')) return path;
            return `${window.location.origin}${path}`;
        },
    },
    mounted() {
        // Initial calculation and responsiveness
        this.scheduleRecalculateRecipients();
        this.scheduleUpdateAttachmentTruncation();
        if (typeof ResizeObserver !== 'undefined') {
            this.resizeObserver = new ResizeObserver(() => {
                this.scheduleRecalculateRecipients();
                this.scheduleUpdateAttachmentTruncation();
            });
            if (this.$refs.recipientContainer) {
                this.resizeObserver.observe(this.$refs.recipientContainer);
            }
            if (this.$refs.attachmentName) {
                this.resizeObserver.observe(this.$refs.attachmentName);
            }
            return;
        }
        window.addEventListener('resize', this.handleResize);
    },
    beforeDestroy() {
        // Cleanup timers and observers
        if (this.recalcTimer) {
            clearTimeout(this.recalcTimer);
            this.recalcTimer = null;
        }
        if (this.attachmentTruncateTimer) {
            clearTimeout(this.attachmentTruncateTimer);
            this.attachmentTruncateTimer = null;
        }
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
            this.resizeObserver = null;
        }
        window.removeEventListener('resize', this.handleResize);
    },
}
</script>

<style scoped>
.email-content ::v-deep p {
    margin-bottom: 1rem;
}

.email-content {
    max-height: var(--email-body-max-height, 320px);
    overflow-y: auto;
    overflow-x: hidden;
    padding-right: 8px;
    scrollbar-gutter: stable;
}

.recipient-text {
    /* Use a single line with ellipsis when the text exceeds available width. */
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    display: inline-block;
}

.recipient-ellipsis {
    white-space: nowrap;
}

.email-header {
    /* Reserve space for the SAMPLE ribbon so header text doesn't run underneath it. */
    padding-right: calc(1rem + var(--ribbon-safe-padding, 0px)) !important;
}

.corner-ribbon {
    position: absolute;
    right: -35px;
    width: 150px;
    padding: 4px 0;
    color: white;
    text-align: center;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 1px;
    transform: rotate(45deg);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
    z-index: 10;
}

.email-preview-card {
    position: relative;
    overflow: hidden;
}

.recipient-line {
    flex: 1 1 auto;
    min-width: 0;
}

.recipient-tooltip {
    max-height: 240px;
    overflow-y: auto;
    min-width: 200px;
    padding-right: 10px;
    box-sizing: border-box;
}

.recipient-tooltip__item {
    font-size: 12px;
    color: #3F3F3F;
    padding: 4px 8px 4px 0;
    border-bottom: 1px solid #F0F0F0;
    overflow-wrap: anywhere;
}

.recipient-tooltip__email {
    color: #3F3F3F;
}

.recipient-tooltip__item:last-child {
    border-bottom: none;
}

.recipient-inactive-label {
    margin-left: 4px;
    color: #F44336;
    font-weight: 600;
}

.email-header-row {
    display: flex;
    align-items: flex-start;
}

.email-header-label {
    width: 60px;
    flex: 0 0 60px;
    display: inline-block;
}

.email-header-value {
    flex: 1 1 auto;
    min-width: 0;
    overflow-wrap: anywhere;
}

.attachment-info {
    flex: 1;
    min-width: 0;
    gap: 8px;
}

.attachment-name {
    display: block;
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.attachment-badge {
    flex-shrink: 0;
    margin-left: 12px;
}

@media (max-width: 768px) {
    .email-content {
        max-height: var(--email-body-max-height-mobile, 260px);
    }
}

</style>

<style>
.attachment-tooltip {
    width: 720px;
    max-width: 90vw !important;
    padding: 6px 10px;
    font-size: 12px;
    line-height: 1.3;
    white-space: normal !important;
    word-break: break-word;
    overflow-wrap: anywhere;
}
</style>
